/**
 * 案件の新規登録の入口: 「URLから{案件}情報を生成する」/「手動で入力する」の 2 枚のカード。
 * 登録ページ (ManualProductNewPage) の入口画面と、案件一覧の「1{案件}ごと登録」で開くモーダル (ProductEntryModal) の共通部品。
 * URL の形式チェックまではここで行い、正規化した URL を onStartUrl に渡す
 */
import { useState } from 'react';
import { brandConfig } from '../../../config/brands';
import { normalizeSummaryUrl, urlSummaryCreditsPerRun } from '../../../features/products/urlSummary';
import { useCreditRates } from '../../../features/organizations/hooks';
import { useCreditBadgeText } from '../../../components/generation/hooks/useGenerationCredits';
import { GenerateButton } from '../../../components/generation/common/GenerateButton';
import { UrlSummaryIcon } from './ProductUrlSummaryModal';
import '../ManualProductNewPage.css';

interface Props {
  /** 正規化済みの URL で読み取りを始める */
  onStartUrl: (url: string) => void;
  onStartManual: () => void;
  /** 読み取りを始められない (利用者 / 組織が未確定) */
  disabled?: boolean;
  initialUrl?: string;
  /** 前回の読み取りの続きがあるとき、そこへ戻るリンクを出す */
  onResume?: () => void;
}

export function ProductEntryChoices({ onStartUrl, onStartManual, disabled = false, initialUrl = '', onResume }: Props) {
  const productLabel = brandConfig.labels.productShort;
  const creditRates = useCreditRates();
  const readCredits = urlSummaryCreditsPerRun(creditRates.data?.rates);
  const readCreditBadge = useCreditBadgeText(readCredits);
  const [entryUrl, setEntryUrl] = useState(initialUrl);
  const [entryUrlError, setEntryUrlError] = useState<string | null>(null);
  const canRead = !!entryUrl.trim() && !disabled && readCredits !== null;

  const startUrl = () => {
    const normalized = normalizeSummaryUrl(entryUrl);
    if (!normalized) {
      setEntryUrlError('URLの形式が正しくありません');
      return;
    }
    setEntryUrlError(null);
    setEntryUrl(normalized);
    onStartUrl(normalized);
  };

  return (
    <section className="new-product-entry" aria-label={`${productLabel}の登録方法`}>
      <div className="new-product-entry-card">
        <h2 className="new-product-entry-title">
          <UrlSummaryIcon size={18} aria-hidden="true" />
          URLから{productLabel}情報を生成する
        </h2>
        <p className="new-product-entry-desc">
          {productLabel}ページのURLを入れると、AIがウェブサイトの情報をもとに{brandConfig.labels.productName}・説明文・ブランドを生成します。
        </p>
        <div className="new-product-entry-url-row">
          <input
            type="url"
            className="inline-edit-input url-summary-input"
            value={entryUrl}
            placeholder="https://example.jp/products/abc"
            onChange={(e) => { setEntryUrl(e.target.value); setEntryUrlError(null); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                if (canRead) startUrl();
              }
            }}
            aria-label="読み取るURL"
          />
          <GenerateButton
            className="btn-ai-director-md"
            label="ページを読み取る"
            creditCost={readCreditBadge}
            onClick={startUrl}
            disabled={!canRead}
            data-testid="new-product-read-url"
          />
        </div>
        {entryUrlError && <div className="url-summary-error" role="alert">{entryUrlError}</div>}
        {onResume && (
          <button type="button" className="btn-link-small" onClick={onResume}>
            前回の読み取りの続きを見る
          </button>
        )}
      </div>
      <div className="new-product-entry-card">
        <h2 className="new-product-entry-title">
          {/* ホームのプロンプト入力と同じ Streamline のキーボード (mask で見出しの文字色に塗る) */}
          <span className="new-product-entry-icon-keyboard" aria-hidden="true" />
          手動で入力する
        </h2>
        <p className="new-product-entry-desc">{productLabel}情報を自分で入力します。</p>
        <div className="new-product-entry-actions">
          <button type="button" className="btn-ai-director-md btn-ai-director-gray" onClick={onStartManual} data-testid="new-product-manual">
            <span className="btn-label">入力へ進む</span>
          </button>
        </div>
      </div>
    </section>
  );
}
